import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api/client';

export interface CostCentreResponse extends Record<string, unknown> {
  cost_centre?: string | null;
}

interface UseCostCentreParams {
  initiatorId?: number;
  enabled?: boolean;
}

export function useCostCentre({ initiatorId, enabled = true }: UseCostCentreParams) {
  return useQuery<CostCentreResponse, Error>({
    queryKey: ['trip-form', 'cost-centre', initiatorId ?? null],
    enabled: Boolean(enabled && initiatorId),
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      if (!initiatorId) {
        throw new Error('Missing initiator ID');
      }

      const response = await apiFetch('/api/v1/business-trip-requests/cost-centre', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          initiator_id: initiatorId,
        }),
      });

      const payload = (await response.json().catch(() => null)) as
        | CostCentreResponse
        | { error?: { message?: unknown } }
        | null;

      if (!response.ok || !payload || typeof payload !== 'object') {
        throw new Error('Failed to fetch cost centre');
      }

      if ('error' in payload && payload.error && typeof payload.error === 'object') {
        const maybeMessage = (payload.error as { message?: unknown }).message;
        if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
          throw new Error(maybeMessage.trim());
        }
        throw new Error('Failed to fetch cost centre');
      }

      return payload as CostCentreResponse;
    },
  });
}
